import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, EVENT_LABEL } from "../lib/api";
import { fmtDateLong, fmtTime } from "../lib/format";
import { Spinner, EmptyState, Button } from "../components/ui";
import { Icon } from "../components/icons";
import { useAuth } from "../auth";
import { can, canSeeAvailability } from "../lib/permissions";
import { SetlistPdfButton, studyQueue } from "../components/StudySetlist";
import { TrackList } from "../components/TrackList";
import { PresenceCard } from "../components/Presence";

const eventBody = (e, song_ids) => ({
  type: e.type, title: e.title, starts_at: e.starts_at, location: e.location || "", address: e.address || "", notes: e.notes || "",
  song_ids, member_ids: e.member_ids || [], setlist_id: e.setlist_id || null,
});

function Meta({ icon, children, testid }) {
  return <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--on-surface-3)", fontSize: 14 }} data-testid={testid}><Icon name={icon} size={16} />{children}</div>;
}

function LinkChip({ to, icon, children, testid }) {
  return <Link to={to} className="chip" data-testid={testid}><Icon name={icon} size={14} /> {children}</Link>;
}

export default function EventPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [err, setErr] = useState("");
  const q = useQuery({ queryKey: ["event", eventId], queryFn: () => api(`/events/${eventId}`) });
  const e = q.data || {};
  const songs = e.songs || [];
  const canEdit = can(user, "events.edit");
  const reorder = useMutation({
    mutationFn: (ids) => {
      const extra = (e.song_ids || []).filter((x) => !ids.includes(x));
      return api(`/admin/events/${e.id}`, { method: "PUT", body: JSON.stringify(eventBody(e, [...ids, ...extra])) });
    },
    onMutate: (ids) => {
      const byId = Object.fromEntries(songs.map((s) => [s.id, s]));
      qc.setQueryData(["event", eventId], { ...e, songs: ids.map((x) => byId[x]) });
    },
    onError: (x) => { setErr(x.message); q.refetch(); },
    onSuccess: () => { setErr(""); qc.invalidateQueries({ queryKey: ["event", eventId] }); qc.invalidateQueries({ queryKey: ["home"] }); },
  });

  if (q.isLoading) return <Spinner />;
  if (q.isError) return (
    <div className="page" style={{ maxWidth: 820 }}>
      <EmptyState icon="cloud-offline-outline" text="Não foi possível carregar o evento." testid="event-error" />
      <Button className="auto" onClick={() => q.refetch()}>TENTAR NOVAMENTE</Button>
    </div>
  );
  const ctx = { key: `event:${e.id}`, label: `Alinhamento · ${e.title || ""}`, path: `/events/${e.id}` };
  return (
    <>
      <div className="page-header" style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <button className="icon-btn" aria-label="Voltar" onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/calendar"))} data-testid="event-back"><Icon name="chevron-back" size={22} /></button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span className="pill doing" data-testid="event-type">{(EVENT_LABEL[e.type] || "Evento").toUpperCase()}</span>
          <h1 className="t-h1" style={{ fontSize: 26, marginTop: 8 }} data-testid="event-title">{e.title}</h1>
        </div>
        {canEdit ? <Button className="auto sm ghost" icon="pencil" onClick={() => navigate(`/admin/events?edit=${e.id}`)} data-testid="event-admin-edit">EDITAR</Button> : null}
      </div>
      <div className="page ulc-fade" style={{ maxWidth: 820, display: "flex", flexDirection: "column", gap: 20 }} data-testid="event-detail">
        <div className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          <Meta icon="calendar-outline" testid="event-date">{fmtDateLong(e.starts_at)}</Meta>
          <Meta icon="time-outline" testid="event-time">{fmtTime(e.starts_at)}</Meta>
          {e.location ? <Meta icon="location-outline" testid="event-location">{[e.location, e.address].filter(Boolean).join(" · ")}</Meta> : null}
          {e.notes ? <div style={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.5, marginTop: 4 }} data-testid="event-notes">{e.notes}</div> : null}
        </div>

        <PresenceCard event={e} />

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="event-links">
          <LinkChip to={`/attendance?evento=${e.id}`} icon="stats" testid="event-link-attendance">A MINHA ASSIDUIDADE</LinkChip>
          {canSeeAvailability(user) ? <LinkChip to={`/events/${e.id}/disponibilidade`} icon="people" testid="event-link-availability">DISPONIBILIDADE</LinkChip> : null}
          {can(user, "attendance.view_all") ? <LinkChip to={`/admin/attendance/${e.id}`} icon="clipboard" testid="event-link-classify">CLASSIFICAR PRESENÇAS</LinkChip> : null}
        </div>

        <div>
          <div className="row-between" style={{ marginBottom: 10 }}>
            <span className="t-section">Alinhamento oficial</span>
            <SetlistPdfButton event={e} testid="event-setlist-pdf" />
          </div>
          {err ? <div style={{ color: "var(--on-error)", marginBottom: 8 }} data-testid="event-reorder-error">{err}</div> : null}
          {e.can_study === false ? <EmptyState icon="list" text="O alinhamento deste evento está reservado aos convocados." /> : (
            <TrackList songs={songs} ctx={ctx} canReorder={canEdit} onReorder={(ids) => reorder.mutate(ids)} buildQueue={() => studyQueue(e)} testid="event-track" emptyText="Este evento ainda não tem alinhamento." />
          )}
        </div>
      </div>
    </>
  );
}
